import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';

export interface CaptchaChallenge {
  provider: 'recaptcha' | 'builtin';
  siteKey: string;
  id: string;
  image: string;
  expiresAt: string;
  required: boolean;
}

export interface CaptchaValue {
  captchaId?: string;
  captchaAnswer?: string;
  /** Google's token, when reCAPTCHA is the challenge in use. */
  captchaToken?: string;
}

/**
 * Sign-in verification challenge.
 *
 * Which one appears is the server's decision, not this component's: it asks,
 * and renders either Google's tick box or the drawn image the API generates
 * itself. A hotel on a server that cannot reach Google still gets a working
 * sign-in screen, which is why the drawn one has not been deleted.
 */
export function Captcha({
  value,
  onChange,
  error,
  disabled,
  reloadToken,
}: {
  value: CaptchaValue;
  onChange: (value: CaptchaValue) => void;
  error?: string;
  disabled?: boolean;
  /** Change this to pull a new challenge, e.g. after a failed sign-in. */
  reloadToken?: number;
}) {
  const [challenge, setChallenge] = useState<CaptchaChallenge | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const next = await api.get<CaptchaChallenge>('/api/public/captcha');
      setChallenge(next);
      onChange(
        next.provider === 'recaptcha'
          ? { captchaToken: '' }
          : { captchaId: next.id, captchaAnswer: '' },
      );
    } catch {
      setFailed(true);
      setChallenge(null);
    } finally {
      setLoading(false);
    }
    // Deliberately no dependency on `onChange`: the parent recreates it every
    // render, and depending on it would refetch a challenge on every keystroke.
  }, []);

  useEffect(() => {
    void load();
  }, [load, reloadToken]);

  // Disabled server-side: render nothing at all rather than an empty box.
  if (!loading && challenge && !challenge.required) return null;

  if (challenge?.provider === 'recaptcha') {
    return (
      <Recaptcha
        siteKey={challenge.siteKey}
        error={error}
        reloadToken={reloadToken}
        onToken={(token) => onChange({ captchaToken: token })}
      />
    );
  }

  return (
    <div className="field captcha">
      <label htmlFor="captcha-answer">Verification</label>

      <div className="captcha-row">
        <div className="captcha-image" aria-hidden="true">
          {loading ? (
            <span className="captcha-loading">…</span>
          ) : failed ? (
            <span className="captcha-loading">unavailable</span>
          ) : (
            <span
              // The SVG is generated by our own API, not user content.
              dangerouslySetInnerHTML={{ __html: challenge?.image ?? '' }}
            />
          )}
        </div>

        <button
          type="button"
          className="btn ghost sm"
          onClick={() => void load()}
          disabled={disabled || loading}
          title="Show a different image"
          aria-label="Show a different verification image"
        >
          ↻
        </button>
      </div>

      <input
        id="captcha-answer"
        className="input"
        autoComplete="off"
        autoCapitalize="characters"
        spellCheck={false}
        maxLength={12}
        placeholder="Type the characters above"
        aria-describedby="captcha-help"
        value={value.captchaAnswer ?? ''}
        disabled={disabled || failed}
        onChange={(e) => onChange({ captchaId: challenge?.id, captchaAnswer: e.target.value })}
      />

      {error ? (
        <span className="field-error">{error}</span>
      ) : (
        <span className="hint" id="captcha-help">
          Not case sensitive. Stuck? Use ↻ for a different image.
        </span>
      )}
    </div>
  );
}

/* ---------------- Google reCAPTCHA ---------------- */

interface RecaptchaApi {
  render: (
    container: HTMLElement,
    options: { sitekey: string; callback: (token: string) => void; 'expired-callback': () => void },
  ) => number;
  reset: (widgetId?: number) => void;
}

declare global {
  interface Window {
    grecaptcha?: RecaptchaApi;
    onKortexRecaptchaLoad?: () => void;
  }
}

const SCRIPT_ID = 'kortex-recaptcha';
const READY_CALLBACK = 'onKortexRecaptchaLoad';

/**
 * Loads Google's script once per page and resolves when the API is usable.
 *
 * Waiting on the script's own `load` event is not enough: it fires while
 * `grecaptcha` is still filling itself in, so `render` is not there yet and the
 * widget looks blocked when it is merely early. Google's documented signal is
 * the `onload` parameter, with a poll behind it for the second component to
 * mount after the script is already on the page.
 */
function loadRecaptcha(): Promise<RecaptchaApi> {
  if (window.grecaptcha?.render) return Promise.resolve(window.grecaptcha);

  return new Promise((resolve, reject) => {
    let settled = false;
    const done = () => {
      if (settled || !window.grecaptcha?.render) return;
      settled = true;
      window.clearInterval(poll);
      window.clearTimeout(giveUp);
      resolve(window.grecaptcha);
    };

    const poll = window.setInterval(done, 50);
    const giveUp = window.setTimeout(() => {
      window.clearInterval(poll);
      if (!settled) {
        settled = true;
        reject(new Error('reCAPTCHA did not become ready'));
      }
    }, 10_000);

    window[READY_CALLBACK] = done;

    if (document.getElementById(SCRIPT_ID)) return;

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = `https://www.google.com/recaptcha/api.js?onload=${READY_CALLBACK}&render=explicit`;
    script.async = true;
    script.defer = true;
    script.addEventListener('error', () => {
      window.clearInterval(poll);
      window.clearTimeout(giveUp);
      if (!settled) {
        settled = true;
        reject(new Error('blocked'));
      }
    });
    document.head.appendChild(script);
  });
}

function Recaptcha({
  siteKey,
  error,
  reloadToken,
  onToken,
}: {
  siteKey: string;
  error?: string;
  reloadToken?: number;
  onToken: (token: string) => void;
}) {
  const host = useRef<HTMLDivElement | null>(null);
  const widget = useRef<number | null>(null);
  const [blocked, setBlocked] = useState(false);

  // The callback is read through a ref so re-rendering the parent does not tear
  // the widget down and put it back — which loses whatever the guest ticked.
  const latest = useRef(onToken);
  latest.current = onToken;

  useEffect(() => {
    let cancelled = false;

    void loadRecaptcha()
      .then((grecaptcha) => {
        if (cancelled || !host.current || widget.current !== null) return;
        widget.current = grecaptcha.render(host.current, {
          sitekey: siteKey,
          callback: (token) => latest.current(token),
          // A token is good for two minutes. When it lapses the tick box clears
          // itself, and the form has to stop believing it holds a good one.
          'expired-callback': () => latest.current(''),
        });
      })
      .catch(() => {
        if (!cancelled) setBlocked(true);
      });

    return () => {
      cancelled = true;
    };
  }, [siteKey]);

  // A token is spent by the attempt that used it, so a failed sign-in needs the
  // tick box put back to how it started.
  useEffect(() => {
    if (widget.current === null || reloadToken === undefined) return;
    window.grecaptcha?.reset(widget.current);
    latest.current('');
  }, [reloadToken]);

  return (
    <div className="field captcha">
      <label>Verification</label>
      <div ref={host} />
      {blocked ? (
        <span className="field-error">
          The verification could not load. Check your connection, or ask your administrator — an
          ad blocker or a firewall may be stopping it.
        </span>
      ) : error ? (
        <span className="field-error">{error}</span>
      ) : (
        <span className="hint">Tick the box to show you are not a robot.</span>
      )}
    </div>
  );
}
